import { Mail } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const PERFECT_FOR = [
  ["Groundnut", "Everyday frying, tadka & crispy snacks"],
  ["Sunflower", "Light daily cooking for the whole family"],
  ["Sesame", "Traditional recipes, pickles & podis"],
  ["Flaxseed", "Drizzling, smoothies & an omega-3 boost"],
  ["Olive", "Salads, pastas & continental cooking"],
  ["Coconut", "Cooking, baking, hair & skin care"],
];

const PACKS = ["500 ml", "1 L", "5 L"];

const perfectFor = (name) =>
  PERFECT_FOR.find(([k]) => name.includes(k))?.[1] || "Everyday healthy cooking";

const mailto = (name) =>
  `mailto:info@tsugivainternational.com?subject=${encodeURIComponent(`Home order enquiry: ${name}`)}&body=${encodeURIComponent(
    `Hi Tsugiva,\n\nI'd like to order ${name} for home use.\nMy city: \nQuantity: \n\nThank you!`
  )}`;

export default function ForHome({ products }) {
  const oils = products.filter((p) => p.category === "oils");

  return (
    <section id="shop" data-testid="for-home-section" className="bg-cream py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="max-w-2xl">
          <Eyebrow>For Your Home</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">
            The same oil we export — bottled for your kitchen
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-smoke sm:text-base">
            Every Tsugiva oil ships to importers worldwide — and the very same batch is available in home
            packs for your family. No checkout carts, no middlemen: email us your city and quantity, and
            we'll confirm availability and delivery personally.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {oils.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06} className="h-full">
              <div
                data-testid={`home-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-stone2 bg-white transition-all duration-500 hover:-translate-y-2 hover:border-gold hover:shadow-[0_24px_60px_-18px_rgba(212,139,22,0.35)]"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-night">
                    Home Packs
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-xl font-semibold text-ink">{p.name}</h3>
                  <p className="mt-1.5 text-sm text-smoke">Perfect for {perfectFor(p.name).toLowerCase()}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {PACKS.map((pack) => (
                      <span key={pack} className="rounded-full border border-stone2 bg-sand px-3 py-1 text-[11px] font-semibold text-botanical">
                        {pack}
                      </span>
                    ))}
                  </div>
                  <a
                    href={mailto(p.name)}
                    data-testid={`home-order-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-botanical px-5 py-2.5 text-sm font-semibold text-botanical transition-all duration-300 hover:bg-botanical hover:text-cream"
                  >
                    <Mail size={15} /> Order via Email
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-10 text-center text-sm text-pebble">
            Home orders are confirmed personally over email — write to{" "}
            <a href="mailto:info@tsugivainternational.com" data-testid="for-home-email-link" className="font-semibold text-gold hover:underline">
              info@tsugivainternational.com
            </a>{" "}
            with your city and quantity.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
